import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Query keys
export const groupingKeys = {
  all: ['grouping'] as const,
  categories: () => [...groupingKeys.all, 'categories'] as const,
  category: (id: string) => [...groupingKeys.categories(), id] as const,
  customerGroups: () => [...groupingKeys.all, 'customer-groups'] as const,
  customerGroup: (id: string) => [...groupingKeys.customerGroups(), id] as const,
  vendorGroups: () => [...groupingKeys.all, 'vendor-groups'] as const,
  vendorGroup: (id: string) => [...groupingKeys.vendorGroups(), id] as const,
};

// Hook to fetch categories with caching
export const useCategories = () => {
  return useQuery({
    queryKey: groupingKeys.categories(),
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: org } = await supabase
        .from('organizations')
        .select('id')
        .eq('owner_id', user.id)
        .single();

      if (!org) throw new Error('Organization not found');

      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('organization_id', org.id)
        .order('name');

      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to fetch customer groups with caching
export const useCustomerGroups = () => {
  return useQuery({
    queryKey: groupingKeys.customerGroups(),
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: org } = await supabase
        .from('organizations')
        .select('id')
        .eq('owner_id', user.id)
        .single();

      if (!org) throw new Error('Organization not found');

      const { data, error } = await supabase
        .from('customer_groups')
        .select('*')
        .eq('organization_id', org.id)
        .order('name');

      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Hook to fetch vendor groups with caching
export const useVendorGroups = () => {
  return useQuery({
    queryKey: groupingKeys.vendorGroups(),
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: org } = await supabase
        .from('organizations')
        .select('id')
        .eq('owner_id', user.id)
        .single();

      if (!org) throw new Error('Organization not found');

      const { data, error } = await supabase
        .from('vendor_groups')
        .select('*')
        .eq('organization_id', org.id)
        .order('name');

      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
